import { useEffect, useMemo } from 'react';
import type { DeltaDocument, NumberingStyle } from '@platform/shared-types';
import { walkDeltaLines } from './deltaOutline';
import { pairLines } from './deltaLineDiff';
import { diffWords } from './wordDiff';
import { ensureDeltaEditorStyles } from './deltaEditorStyles';

/**
 * THE REDLINE — what changed between two versions of a document.
 *
 * Read-only, always. Editing happens in `DeltaEditor` on a clean document;
 * this view exists so a reviewer can see exactly what moved before approving.
 * The version chain is the change record, so nothing here writes anything.
 *
 * ── HOW THE DIFF IS ALIGNED ──────────────────────────────────────────────
 *
 * A naive word diff over the two documents' whole plain text produces a
 * correct but unreadable result: one changed word early on shifts the
 * alignment and the rest of the contract renders as one enormous
 * insert-plus-delete.
 *
 * So the diff is aligned BY LINE first. Lines are matched with a
 * longest-common-subsequence over their exact text — the same shape as
 * `diffWords`, one level up — and only lines that PAIR are diffed word by
 * word. An unpaired line is wholly added or wholly removed, which is what it
 * actually is.
 *
 * That also gives the per-section change marks their meaning: a section is
 * "changed" when any line inside it changed, rather than because the diff
 * drifted out of alignment somewhere above it.
 */

const HEADING_TAG: Record<number, 'h1' | 'h2' | 'h3'> = { 1: 'h1', 2: 'h2', 3: 'h3' };

export interface DeltaCompareSummary {
  wordsAdded: number;
  wordsRemoved: number;
  linesChanged: number;
  sectionsTouched: number;
  sectionsTotal: number;
}

export interface DeltaCompareProps {
  before: DeltaDocument | null | undefined;
  after: DeltaDocument | null | undefined;
  beforeLabel?: string;
  afterLabel?: string;
  numbering?: NumberingStyle;
  /** Reported upward so the host can render counts beside the view. */
  onSummary?: (s: DeltaCompareSummary) => void;
  className?: string;
}

export function DeltaCompare({
  before, after, numbering = 'none', onSummary, className = '',
}: DeltaCompareProps) {
  ensureDeltaEditorStyles();

  const { pairs, summary } = useMemo(() => {
    const bl = walkDeltaLines(before).filter((l) => !l.isBlock);
    const al = walkDeltaLines(after).filter((l) => !l.isBlock);
    const ps = pairLines(bl, al);

    let wordsAdded = 0;
    let wordsRemoved = 0;
    let linesChanged = 0;
    const touched = new Set<number>();
    let sectionIdx = -1;
    let sectionsTotal = 0;

    for (const p of ps) {
      const line = p.after ?? p.before!;
      if (line.attrs.header !== undefined) { sectionIdx += 1; sectionsTotal += 1; }

      if (p.kind === 'same') continue;
      linesChanged += 1;
      if (sectionIdx >= 0) touched.add(sectionIdx);

      if (p.kind === 'added') wordsAdded += countWords(p.after!.text);
      else if (p.kind === 'removed') wordsRemoved += countWords(p.before!.text);
      else {
        for (const op of diffWords(p.before!.text, p.after!.text)) {
          if (op.kind === 'added') wordsAdded += countWords(op.text);
          if (op.kind === 'removed') wordsRemoved += countWords(op.text);
        }
      }
    }

    return {
      pairs: ps,
      summary: {
        wordsAdded, wordsRemoved, linesChanged,
        sectionsTouched: touched.size, sectionsTotal,
      } as DeltaCompareSummary,
    };
  }, [before, after]);

  // An effect, never a memo: reporting upward is a side effect, and running
  // it during render would set state on the parent mid-render.
  useEffect(() => { onSummary?.(summary); }, [summary, onSummary]);

  // Section numbers are derived over the AFTER document, since that is the
  // version being reviewed. A removed heading therefore shows no number,
  // which is correct: it has no position in the document under review.
  let major = 0;
  let minor = 0;
  const numberFor = (level: number, present: boolean): string => {
    if (numbering === 'none' || level === 1 || !present) return '';
    if (level === 2) { major += 1; minor = 0; return `${major}.`; }
    if (numbering === 'decimal') return '';
    minor += 1;
    return `${major}.${minor}`;
  };

  return (
    <div className={`dq-page ${className}`}>
      <div
        className="px-[78px] py-16 font-serif text-[15.5px] leading-[1.72] text-stone-900"
        style={{ fontFamily: "'Iowan Old Style','Palatino Linotype',Palatino,Georgia,'Times New Roman',serif" }}
      >
        {pairs.map((p, i) => {
          const line = p.after ?? p.before!;
          const level = line.attrs.header;
          const num = numberFor(level ?? 0, p.kind !== 'removed');

          const body = p.kind === 'changed'
            ? diffWords(p.before!.text, p.after!.text).map((op, k) => (
              <span
                key={k}
                className={op.kind === 'added' ? 'dq-ins' : op.kind === 'removed' ? 'dq-del' : undefined}
              >
                {op.text}
              </span>
            ))
            : (
              <span className={p.kind === 'added' ? 'dq-ins' : p.kind === 'removed' ? 'dq-del' : undefined}>
                {line.text}
              </span>
            );

          const mark = p.kind === 'same' ? null : (
            <span
              aria-hidden="true"
              className={`absolute -left-[92px] top-[0.42em] w-[38px] text-right text-[9.5px] font-extrabold tracking-wider ${
                p.kind === 'removed' ? 'text-red-600' : p.kind === 'added' ? 'text-emerald-600' : 'text-amber-500'
              }`}
            >
              {p.kind === 'removed' ? 'REMOVED' : p.kind === 'added' ? 'NEW' : 'CHANGED'}
            </span>
          );

          const secno = num ? (
            <span
              aria-hidden="true"
              className="absolute -left-[46px] top-[0.32em] w-[38px] text-right font-sans text-[12.5px] tabular-nums text-stone-400"
            >
              {num}
            </span>
          ) : null;

          const common = 'relative';

          if (level !== undefined) {
            const Tag = HEADING_TAG[level] ?? 'h2';
            const size = level === 1
              ? 'text-[23px] text-center font-semibold mt-0 mb-4'
              : level === 2 ? 'text-[16.5px] font-semibold mt-7 mb-2' : 'text-[14.8px] font-semibold mt-5 mb-1.5';
            return (
              <Tag key={i} className={`${common} ${size}`}>
                {secno}{mark}{body}
              </Tag>
            );
          }

          if (line.text === '') return <div key={i} className="h-3" />;

          return (
            <p key={i} className={`${common} mb-3`}>
              {secno}{mark}{body}
            </p>
          );
        })}

        {pairs.length === 0 && (
          <p className="font-sans text-[12.5px] text-stone-400">
            Nothing to compare — one of the two versions has no continuous-document body.
          </p>
        )}
      </div>
    </div>
  );
}

function countWords(s: string): number {
  const t = s.trim();
  return t === '' ? 0 : t.split(/\s+/).length;
}

export default DeltaCompare;
